import React from 'react';

import {
    getFromLocalStorage,
    saveToLocalStorage,
    getForUserContextUnencrypted,
    saveForUserContextUnencrypted,
} from '@famly/stat_ts-utils_local-storage';

/**
 * Use localstorage in a "synced" way.
 *
 * Usage:
 * ```
 * const [value, setValue] = useLocalStorageValue(LOCAL_STORAGE_KEY);
 *
 * setValue(newValue)
 * ```
 *
 * When `setValue` is used, the passed argument will be saved to local storage
 * and your component will be updated via `value`.
 */
export const useLocalStorageValue = (key: string) => {
    const [value, _setValue] = React.useState(() => {
        return getFromLocalStorage(key);
    });

    const setValue = React.useCallback(
        <T>(value: T) => {
            saveToLocalStorage(key, value);
            _setValue(value);
        },
        [key],
    );

    return [value, setValue] as const;
};

export const useLocalStorageValueForContext = (key: string) => {
    const [value, _setValue] = React.useState(() => {
        return getForUserContextUnencrypted(key);
    });

    const setValue = React.useCallback(
        <T>(value: T) => {
            saveForUserContextUnencrypted(key, value);
            _setValue(value);
        },
        [key],
    );

    return [value, setValue] as const;
};
